import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { SmartLink } from "../lib/navLink.jsx";

/**
 * Unified feature section — full-width background (gradient or image) with
 * text on one side and media/card on the other.
 *
 * section props:
 *   id, kicker, title, body, meta, bullets, ctaLabel, ctaHref
 *
 *   mediaSide: "end" (right, default) | "start" (left)
 *
 *   background: {
 *     type: "gradient" (default) | "image" | "light",
 *     gradient,   // CSS string override for gradient type
 *     imageUrl,   // required for image type
 *     imageAlt,
 *   }
 *
 *   card: {                          // omit for plain image media
 *     type: "feature" | "collage" | "image",
 *     imageUrl, imageAlt, badge, meta, title, body, linkLabel, linkHref,
 *     category,
 *   }
 *
 *   imageUrl, imageAlt              // plain section-level media (no card wrapper)
 */
export default function SectionOverlap({ section }) {
  if (!section?.title) return null;
  const {
    id, kicker, title, body, meta, bullets, ctaLabel, ctaHref,
    background, card, mediaSide, imageUrl, imageAlt,
  } = section;

  const bgType = background?.type ?? "gradient";
  let bgStyle = {};
  let extraClass = "";

  if (bgType === "image" && background?.imageUrl) {
    bgStyle = {
      backgroundImage: `url(${background.imageUrl})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
    extraClass = " pmp-overlap-section--img-bg";
  } else if (bgType === "light") {
    extraClass = " pmp-overlap-section--light";
  } else {
    bgStyle = {
      background:
        background?.gradient ??
        "linear-gradient(135deg, #0d1b2e 0%, #1a4d9e 60%, #1a2d4a 100%)",
    };
  }

  const mediaOnLeft = ["start", "left"].includes(
    String(mediaSide ?? "end").trim().toLowerCase()
  );

  const textCol = (
    <Col lg={6}>
      {kicker && <p className="pmp-kicker pmp-overlap-kicker mb-3">{kicker}</p>}
      <h2 className="pmp-overlap-title">{title}</h2>
      {body && <p className="pmp-overlap-body">{body}</p>}
      {bullets?.length > 0 && (
        <ul className="pmp-overlap-bullets">
          {bullets.map((b, i) => <li key={i}>{b}</li>)}
        </ul>
      )}
      {meta && <p className="pmp-overlap-meta">{meta}</p>}
      {ctaLabel && (
        <SmartLink href={ctaHref || "#"} className="pmp-overlap-cta">
          {ctaLabel}
          <span className="bi bi-arrow-right ms-1" aria-hidden="true" />
        </SmartLink>
      )}
    </Col>
  );

  const mediaCol = (
    <Col lg={6}>
      {card ? (
        <div className="pmp-overlap-float-card">
          {(!card.type || card.type === "feature") && <FeatureCard card={card} />}
          {card.type === "collage" && <CollageCard card={card} />}
          {card.type === "image" && <ImageCard card={card} />}
        </div>
      ) : (
        <div className="pmp-overlap-plain-media">
          {imageUrl ? (
            <img src={imageUrl} alt={imageAlt ?? ""} />
          ) : (
            <div className="pmp-overlap-plain-media-placeholder">{imageAlt ?? "Media"}</div>
          )}
        </div>
      )}
    </Col>
  );

  return (
    <section className={`pmp-overlap-section${extraClass}`} id={id} style={bgStyle}>
      <Container>
        <Row className={`align-items-center g-5${mediaOnLeft ? " flex-lg-row-reverse" : ""}`}>
          {textCol}
          {mediaCol}
        </Row>
      </Container>
    </section>
  );
}

function FeatureCard({ card }) {
  const { imageUrl, imageAlt, badge, meta, title, body, linkLabel, linkHref } = card;
  return (
    <div className="pmp-overlap-card">
      <div className="pmp-overlap-card-img">
        {imageUrl ? (
          <img src={imageUrl} alt={imageAlt ?? title ?? ""} />
        ) : (
          <div className="pmp-overlap-card-img-placeholder">
            {imageAlt ?? title}
          </div>
        )}
        {badge && <span className="pmp-overlap-card-badge">{badge}</span>}
      </div>
      <div className="pmp-overlap-card-body">
        {meta && <p className="pmp-overlap-card-meta">{meta}</p>}
        {title && <h3 className="pmp-overlap-card-title">{title}</h3>}
        {body && <p className="pmp-overlap-card-desc">{body}</p>}
        {(linkLabel || linkHref) && (
          <SmartLink href={linkHref || "#"} className="pmp-overlap-card-link">
            {linkLabel ? `${linkLabel} ` : ""}&#x2192;
          </SmartLink>
        )}
      </div>
    </div>
  );
}

function CollageCard({ card }) {
  const { imageUrl, imageAlt, badge, category, meta, title, body, linkLabel, linkHref } = card;
  const badgeText = badge ?? category;
  return (
    <SmartLink
      href={linkHref || "#"}
      className="pmp-collage-card-link d-block text-decoration-none"
    >
      <div className="pmp-collage-card">
        <div className="pmp-collage-card-img-col">
          {imageUrl ? (
            <img src={imageUrl} alt={imageAlt ?? title ?? ""} />
          ) : (
            <div className="pmp-collage-card-img-placeholder">
              {imageAlt ?? badgeText}
            </div>
          )}
          {badgeText && (
            <span className="pmp-collage-card-badge">{badgeText}</span>
          )}
        </div>
        <div className="pmp-collage-card-body">
          {meta && <p className="pmp-overlap-card-meta">{meta}</p>}
          {title && <h3 className="pmp-collage-card-title">{title}</h3>}
          {body && <p className="pmp-collage-card-desc">{body}</p>}
          <span className="pmp-collage-card-arrow">
            {linkLabel ? `${linkLabel} ` : ""}&#x2192;
          </span>
        </div>
      </div>
    </SmartLink>
  );
}

function ImageCard({ card }) {
  const { imageUrl, imageAlt } = card;
  return (
    <div className="pmp-overlap-img-only">
      {imageUrl ? (
        <img src={imageUrl} alt={imageAlt ?? ""} />
      ) : (
        <div className="pmp-overlap-img-placeholder">{imageAlt}</div>
      )}
    </div>
  );
}
